/**
 * PPLX.2 — Quote × AI-Research Merge.
 *
 * Vor dem Score-Compute wird der Provider-Quote mit recherchierten
 * AI-Werten gemerged: Provider-Wert hat Vorrang, KI-Wert ist Fallback bei null.
 *
 * Damit zieht die Score-Engine automatisch AI-Daten als Fundament-Bausteine,
 * ohne dass scorers.ts geändert werden muss.
 */

import type { PriceQuote } from './market-data/types';
import type { AIResearchData } from './ai-research';

function pick<T>(provider: T | null | undefined, ai: T | null | undefined): T | null {
  if (provider !== null && provider !== undefined) return provider as T;
  if (ai !== null && ai !== undefined) return ai as T;
  return null;
}

/**
 * W67 Phase 4 (2026-05-25) — Sanity-Check fuer AI-recherchierte Werte.
 * Verhindert dass Perplexity-Halluzinationen (SPGI ROIC 112%, BRK.B market_cap 1.03)
 * stillschweigend in Score + Datenvertrauen einfliessen.
 *
 * Regeln (konservativ — nur extreme Ausreisser blacklisten):
 *   - ROIC > 60 %     → halluziniert (selbst Mastercard hat ~30 %)
 *   - ROE > 150 %     → halluziniert (Exception: hochverschuldete Companies bis 80, danach unsicher)
 *   - market_cap < 1 (USD Mrd.) → halluziniert (Watchlist enthaelt nur Mid+ Caps)
 *   - high_52w deviation > 30 % vs OHLCV-derived → halluziniert
 */
function sanitizeAIValue(
  field: 'roic' | 'roe' | 'market_cap' | 'high_52w',
  value: number | null | undefined,
  context?: { ohlcv_high_52w?: number | null }
): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  switch (field) {
    case 'roic':
      return value > 60 ? null : value;
    case 'roe':
      return value > 150 ? null : value;
    case 'market_cap':
      // Watchlist enthaelt nur ernsthafte Werte — alles < 1 Mrd. ist Halluzination
      return value < 1 ? null : value;
    case 'high_52w': {
      const ref = context?.ohlcv_high_52w;
      if (ref === null || ref === undefined || ref <= 0) return value; // kein Vergleich moeglich
      const deviation = Math.abs(value - ref) / ref;
      return deviation > 0.3 ? null : value;
    }
  }
}

/**
 * W67 Phase 3 — Optionale OHLCV-derived Werte als HOECHSTE-PRIORITY-Quelle.
 * Wenn vorhanden, ueberschreiben sie auch Provider-Werte (OHLCV-Bars sind
 * deterministisch und meist aktueller/genauer als Provider-Profile-Calls).
 */
export interface OhlcvDerivedOverrides {
  high_52w_from_bars: number | null;
  low_52w_from_bars: number | null;
  drawdown_from_52w_high_pct_from_bars: number | null;
  // W67 Phase 5b — Returns aus OHLCV-Bars als Fallback wenn Provider null liefert
  // (TwelveData + Tiingo geben diese Felder nicht zurueck, nur Finnhub).
  return_13w_pct_from_bars?: number | null;
  return_26w_pct_from_bars?: number | null;
  return_52w_pct_from_bars?: number | null;
}

/**
 * Liefert ein neues PriceQuote-Objekt, in dem alle null-Felder mit AI-Werten
 * (sofern vorhanden) befüllt sind. Provider-Werte werden NIE überschrieben,
 * AUSSER: 52W-Hoch/Tief/Drawdown, wenn OHLCV-Bars-derived Werte uebergeben
 * werden — die sind die deterministische SSoT.
 */
export function mergeQuoteWithAI(
  quote: PriceQuote,
  ai: AIResearchData | null,
  ohlcv?: OhlcvDerivedOverrides | null
): PriceQuote {
  // W67 Phase 3 — 52W-Prioritaet:
  //   1) OHLCV-Bars-derived (deterministisch, aktuell)
  //   2) Provider-Quote (high_52w-Feld)
  //   3) AI-Cache (ai_high_52w_usd)
  // Fuer low_52w + drawdown_from_52w_high_pct analog. low_52w wurde vorher nie
  // gemerged → der "Jahrestief"-Stat zeigt bei AI-Quotes ohne Provider-Low blank.
  const ohlcvHigh = ohlcv?.high_52w_from_bars ?? null;
  const ohlcvLow = ohlcv?.low_52w_from_bars ?? null;
  const ohlcvDrawdown = ohlcv?.drawdown_from_52w_high_pct_from_bars ?? null;

  // W99-Hotfix — Share-Class-Guard für den PROVIDER-high_52w. Finnhub
  // liefert für BRK.B das A-Share-Hoch (~775.000 statt ~516): die Zelle
  // zeigte „−99,9 % unter Hoch", seit W97 grün = falsches Kaufsignal.
  // Verworfen wird, wenn der Wert >5× über dem Bars-Hoch liegt (kein
  // echtes 52W-Hoch kann 5× über dem höchsten Tages-HOCH liegen) bzw.
  // ohne Bars-Referenz >6× über dem aktuellen Kurs (−83 %-Implikation —
  // bei Aktien praktisch immer Datenfehler; lieber kein Wert als Müll).
  const providerHigh52w = (() => {
    const v = quote.high_52w;
    if (v === null || v === undefined || !Number.isFinite(v) || v <= 0) return null;
    if (ohlcvHigh !== null && ohlcvHigh > 0) return v > ohlcvHigh * 5 ? null : v;
    if (quote.current_price > 0 && v > quote.current_price * 6) return null;
    return v;
  })();

  // W67 Phase 4 — AI-high_52w gegen OHLCV-Referenz validieren (verhindert
  // SPGI-Halluzination $984 statt $540).
  const sanitizedAiHigh52w = sanitizeAIValue('high_52w', ai?.ai_high_52w_usd, {
    ohlcv_high_52w: ohlcvHigh,
  });
  const effectiveHigh52w =
    ohlcvHigh !== null && ohlcvHigh > 0
      ? ohlcvHigh
      : providerHigh52w !== null
        ? providerHigh52w
        : sanitizedAiHigh52w && sanitizedAiHigh52w > 0
          ? sanitizedAiHigh52w
          : providerHigh52w;
  const effectiveLow52w =
    ohlcvLow !== null && ohlcvLow > 0
      ? ohlcvLow
      : quote.low_52w && quote.low_52w > 0
        ? quote.low_52w
        : quote.low_52w;
  // W99-Hotfix — der Provider-Drawdown ist aus dem Provider-High gerechnet:
  // wurde das High verworfen, ist auch der Drawdown Müll (BRK.B −99,9 %).
  const effectiveDrawdown =
    ohlcvDrawdown !== null
      ? ohlcvDrawdown
      : providerHigh52w !== null &&
          quote.drawdown_from_52w_high_pct !== null &&
          quote.drawdown_from_52w_high_pct !== undefined
        ? quote.drawdown_from_52w_high_pct
        : effectiveHigh52w !== null && effectiveHigh52w > 0 && quote.current_price > 0
          ? ((quote.current_price - effectiveHigh52w) / effectiveHigh52w) * 100
          : null;

  // W67 Phase 5b — Returns aus OHLCV als Fallback wenn Quote null liefert.
  // Provider-Wert hat Vorrang (frischer), OHLCV-Wert ist Backup.
  const pickReturn = (providerVal: number | null | undefined, ohlcvVal: number | null | undefined) =>
    providerVal !== null && providerVal !== undefined && Number.isFinite(providerVal)
      ? providerVal
      : ohlcvVal ?? null;
  const effectiveReturn13w = pickReturn(quote.return_13w_pct, ohlcv?.return_13w_pct_from_bars);
  const effectiveReturn26w = pickReturn(quote.return_26w_pct, ohlcv?.return_26w_pct_from_bars);
  const effectiveReturn52w = pickReturn(quote.return_52w_pct, ohlcv?.return_52w_pct_from_bars);

  if (!ai) {
    // Trotzdem die OHLCV-Werte applizieren wenn vorhanden — und NICHT früh
    // raus, wenn der W99-Guard den Provider-high_52w verworfen hat.
    if (
      ohlcvHigh === null &&
      ohlcvLow === null &&
      ohlcvDrawdown === null &&
      (effectiveHigh52w ?? 0) === quote.high_52w &&
      effectiveReturn13w === quote.return_13w_pct &&
      effectiveReturn26w === quote.return_26w_pct &&
      effectiveReturn52w === quote.return_52w_pct
    )
      return quote;
    return {
      ...quote,
      // 0 = „kein Wert" (Codebase-Idiom: alle Konsumenten prüfen > 0)
      high_52w: effectiveHigh52w ?? 0,
      low_52w: effectiveLow52w,
      drawdown_from_52w_high_pct: effectiveDrawdown,
      return_13w_pct: effectiveReturn13w,
      return_26w_pct: effectiveReturn26w,
      return_52w_pct: effectiveReturn52w,
    };
  }

  return {
    ...quote,
    high_52w: effectiveHigh52w ?? 0,
    low_52w: effectiveLow52w,
    drawdown_from_52w_high_pct: effectiveDrawdown,
    return_13w_pct: effectiveReturn13w,
    return_26w_pct: effectiveReturn26w,
    return_52w_pct: effectiveReturn52w,
    pe_ttm: pick(quote.pe_ttm, ai.ai_pe_ttm),
    // W67 Phase 4 — ROE/ROIC gegen Halluzinations-Schwellen filtern (siehe sanitizeAIValue)
    roe_ttm_pct: pick(quote.roe_ttm_pct, sanitizeAIValue('roe', ai.ai_roe_ttm_pct)),
    net_profit_margin_pct: pick(quote.net_profit_margin_pct, ai.ai_net_profit_margin_pct),
    operating_margin_pct: pick(quote.operating_margin_pct, ai.ai_operating_margin_pct),
    roa_ttm_pct: pick(quote.roa_ttm_pct, ai.ai_roa_ttm_pct),
    debt_to_equity: pick(quote.debt_to_equity, ai.ai_debt_to_equity),
    current_ratio: pick(quote.current_ratio, ai.ai_current_ratio),
    price_to_book: pick(quote.price_to_book, ai.ai_price_to_book),
    price_to_sales: pick(quote.price_to_sales, ai.ai_price_to_sales),
    beta: pick(quote.beta, ai.ai_beta),
    revenue_growth_5y_pct: pick(quote.revenue_growth_5y_pct, ai.ai_revenue_growth_5y_pct),
    eps_growth_5y_pct: pick(quote.eps_growth_5y_pct, ai.ai_eps_growth_5y_pct),
    dividend_yield_pct: pick(quote.dividend_yield_pct, ai.ai_dividend_yield_pct),
    // V6-DATA.6 — neue Pflichtfelder direkt aus AI-Cache (Provider liefert sie nicht)
    fcf_ttm_usd: pick(quote.fcf_ttm_usd, ai.ai_fcf_ttm_usd),
    fcf_margin_pct: pick(quote.fcf_margin_pct, ai.ai_fcf_margin_pct),
    roic_ttm_pct: pick(quote.roic_ttm_pct, sanitizeAIValue('roic', ai.ai_roic_ttm_pct)),
    payout_ratio_pct: pick(quote.payout_ratio_pct, ai.ai_payout_ratio_pct),
    dividend_growth_5y_pct: pick(quote.dividend_growth_5y_pct, ai.ai_dividend_growth_5y_pct),
    // V6-DATA.7 — Score-Block-Lücken (market_cap aus KI als Fallback)
    market_cap_billions: pick(quote.market_cap_billions, sanitizeAIValue('market_cap', ai.ai_market_cap_usd_b)),
    // high_52w + drawdown wurden oben bereits berechnet
  };
}
